
class ToolManagementCapability {
  constructor(planStateSetters, planGetters, savePlan) {
    this.setters = planStateSetters;
    this.getters = planGetters;
    this.savePlan = savePlan;
  }

  async handleAddToolsToStages(action) {
    console.log("➕ ToolManagementCapability: Tool addition started:", action);
    let targetData = action;

    // Handle different action structures
    if (action.data) {
      targetData = action.data;
    }

    // Handle component_capability wrapper
    if (action.type === "component_capability" && action.data && action.data.capability === "add_tools_to_stages") {
      targetData = action.data.data;
    }

    // Handle bulk_actions wrapper
    if (action.capability === "add_tools_to_stages" && action.data) {
      targetData = action.data;
    }

    // FIX #38: Handle bulk program merge (when data.programs exists with merge_with_existing)
    if (targetData.merge_with_existing && targetData.programs && Array.isArray(targetData.programs)) {
      console.log("➕ FIX #38: Bulk program merge mode - merging tools from programs data");
      return await this.handleBulkProgramMerge(targetData);
    }

    const { programIndex: addProgIdx, stage: addStage, tools: toolsToAdd } = targetData;

    // Validate input data
    if (addProgIdx === undefined || !addStage || !toolsToAdd || !Array.isArray(toolsToAdd)) {
      console.error("❌ Invalid tool addition data:", { addProgIdx, addStage, toolsToAdd });
      return { 
        success: false, 
        message: "Invalid data provided for tool addition",
        error: true,
        debugInfo: {
          addProgIdx,
          addStage,
          toolsToAdd: toolsToAdd ? toolsToAdd.length : 'null',
          toolsIsArray: Array.isArray(toolsToAdd)
        }
      };
    }

    // Validate program index exists
    const currentPrograms = this.getters.finalProgramSequence || [];
    if (addProgIdx >= currentPrograms.length) {
      console.error("❌ Program index out of bounds:", { addProgIdx, totalPrograms: currentPrograms.length });
      return {
        success: false,
        message: `Program index ${addProgIdx} doesn't exist. Only ${currentPrograms.length} programs available.`,
        error: true,
        debugInfo: {
          addProgIdx,
          totalPrograms: currentPrograms.length,
          availablePrograms: currentPrograms.map((p, i) => ({ index: i, name: p.program_of_work }))
        }
      };
    }

    console.log(`➕ BEFORE ADDITION: Adding ${toolsToAdd.length} tools to Program[${addProgIdx}].${addStage}`);
    console.log(`➕ Tools to add:`, toolsToAdd.map(t => t.tool || t.name).join(', '));

    // Helper function to add tools to a program
    const addToolsToProgram = (program) => {
      console.log(`➕ Processing program: ${program?.program_of_work}`);

      if (!program) {
        console.warn(`➕ Program at index ${addProgIdx} not found`);
        return program;
      }

      // Create deep copy to avoid mutations
      const updatedProgram = { ...program };
      
      // Initialize toolsByStage if it doesn't exist (must check BEFORE spreading)
      if (!program.toolsByStage) {
        updatedProgram.toolsByStage = {};
      } else {
        updatedProgram.toolsByStage = { ...program.toolsByStage };
      }

      // Initialize stage if it doesn't exist
      if (!updatedProgram.toolsByStage[addStage]) {
        updatedProgram.toolsByStage[addStage] = { tools: [], totalDuration: 0 };
      }

      updatedProgram.toolsByStage[addStage] = { ...updatedProgram.toolsByStage[addStage] };
      
      // Ensure tools array exists before accessing length
      if (!updatedProgram.toolsByStage[addStage].tools) {
        updatedProgram.toolsByStage[addStage].tools = [];
      }
      const originalToolsCount = updatedProgram.toolsByStage[addStage].tools.length;
      console.log(`➕ Original tools count in ${addStage}: ${originalToolsCount}`);

      // Add new tools
      const newTools = toolsToAdd.map((tool, index) => ({
        id: `tool-${Date.now()}-${index}`,
        tool: tool.tool || tool.name,
        description: tool.description || `${tool.tool || tool.name} tool description`,
        duration: tool.duration || 1,
        related_stage: addStage,
        complexity: tool.complexity || "medium"
      }));

      updatedProgram.toolsByStage[addStage].tools = [...updatedProgram.toolsByStage[addStage].tools, ...newTools];

      const newToolsCount = updatedProgram.toolsByStage[addStage].tools.length;
      const addedCount = newToolsCount - originalToolsCount;
      console.log(`➕ Tools added: ${addedCount}, total now: ${newToolsCount}`);

      // Recalculate stage total duration
      const oldStageDuration = updatedProgram.toolsByStage[addStage].totalDuration;
      updatedProgram.toolsByStage[addStage].totalDuration = updatedProgram.toolsByStage[addStage].tools.reduce(
        (sum, t) => sum + (t.duration || 0), 0
      );

      console.log(`➕ Stage ${addStage} duration: ${oldStageDuration} → ${updatedProgram.toolsByStage[addStage].totalDuration}`);

      // Recalculate program total duration
      const oldProgramDuration = updatedProgram.totalProgramLength;
      updatedProgram.totalProgramLength = Object.values(updatedProgram.toolsByStage).reduce(
        (sum, stageData) => sum + (stageData.totalDuration || 0), 0
      );

      console.log(`➕ Program ${updatedProgram.program_of_work} duration: ${oldProgramDuration} → ${updatedProgram.totalProgramLength}`);

      // Add stage to Stages array if not present
      if (updatedProgram.Stages && !updatedProgram.Stages.includes(addStage)) {
        updatedProgram.Stages = [...updatedProgram.Stages, addStage];
        console.log(`➕ Added ${addStage} to Stages array`);
      }

      return updatedProgram;
    };

    let updatedSequence = null;
    let newTotalDuration = 0;
    let updateSuccess = false;

    // Update finalProgramSequence and capture the updated sequence
    console.log("➕ Updating finalProgramSequence...");
    this.setters.setFinalProgramSequence(prevSequence => {
      console.log("➕ setFinalProgramSequence callback executing...");
      updatedSequence = [...prevSequence];
      const oldProgram = updatedSequence[addProgIdx];
      updatedSequence[addProgIdx] = addToolsToProgram(updatedSequence[addProgIdx]);

      // Verify the update was successful
      if (updatedSequence[addProgIdx] !== oldProgram) {
        updateSuccess = true;
        console.log("➕ ✅ Program object reference changed - update successful");
      }

      // Calculate total duration from the updated sequence
      const oldTotalDuration = prevSequence.reduce((sum, program) => 
        sum + (program.totalProgramLength || 0), 0
      );
      newTotalDuration = updatedSequence.reduce((sum, program) => 
        sum + (program.totalProgramLength || 0), 0
      );

      console.log(`➕ Total duration change: ${oldTotalDuration} → ${newTotalDuration}`);
      console.log("➕ Updated finalProgramSequence callback complete");

      return updatedSequence;
    });

    // Update selectedPrograms with the same data
    console.log("➕ Updating selectedPrograms...");
    this.setters.setSelectedPrograms(prevSelected => {
      console.log("➕ setSelectedPrograms callback executing...");
      const updatedSelected = [...prevSelected];
      if (updatedSelected[addProgIdx]) {
        updatedSelected[addProgIdx] = addToolsToProgram(updatedSelected[addProgIdx]);
        console.log("➕ selectedPrograms updated");
      } else {
        console.warn(`➕ Program at index ${addProgIdx} not found in selectedPrograms`);
      }
      return updatedSelected;
    });

    // Update total duration with the calculated value
    console.log(`➕ Updating total duration to: ${newTotalDuration}`);
    this.setters.setTotalDuration(newTotalDuration);

    // Mark as unsaved to trigger auto-save
    console.log("➕ Marking plan as unsaved");
    this.setters.setIsSaved(false);

    // Force a save after a delay to ensure persistence
    setTimeout(() => {
      console.log("➕ Triggering delayed save after tool addition");
      if (this.savePlan && typeof this.savePlan === 'function') {
        this.savePlan();
      }
    }, 1000);

    // Return detailed result - ENHANCED REPORTING
    const result = {
      success: true, // Always success if we reach this point without exceptions
      message: `Successfully added ${toolsToAdd.length} tools to ${addStage} stage`,
      addedTools: toolsToAdd,
      updatedProgram: updatedSequence[addProgIdx],
      newTotalDuration: newTotalDuration,
      stage: addStage,
      programIndex: addProgIdx
    };

    console.log(`➕ ${updateSuccess ? '✅' : '❌'} Tool addition ${updateSuccess ? 'complete' : 'failed'}:`, result);

    return result;
  }

  async handleRemoveToolsFromStages(data) {
    console.log("🗑️ ToolManagementCapability: Tool removal started:", data);
    const { programIndex: progIdx, stage: stageToModify, tools: toolsToRemove } = data;

    // Validate input data
    if (progIdx === undefined || !stageToModify || !toolsToRemove || !Array.isArray(toolsToRemove)) {
      console.error("❌ Invalid tool removal data:", { progIdx, stageToModify, toolsToRemove });
      return { success: false, message: "Invalid data provided for tool removal" };
    }

    console.log(`🗑️ BEFORE REMOVAL: Removing tools [${toolsToRemove.join(', ')}] from Program[${progIdx}].${stageToModify}`);

    // Helper function to remove tools from a program
    const removeToolsFromProgram = (program) => {
      console.log(`🗑️ Processing program: ${program?.program_of_work}`);

      if (!program?.toolsByStage?.[stageToModify]?.tools) {
        console.warn(`🗑️ Stage ${stageToModify} not found in program ${program?.program_of_work}`);
        return program;
      }

      // Create deep copy to avoid mutations
      const updatedProgram = { ...program };
      updatedProgram.toolsByStage = { ...program.toolsByStage };
      updatedProgram.toolsByStage[stageToModify] = { ...updatedProgram.toolsByStage[stageToModify] };

      const originalToolsCount = updatedProgram.toolsByStage[stageToModify].tools.length;
      console.log(`🗑️ Original tools count in ${stageToModify}: ${originalToolsCount}`);
      console.log(`🗑️ Original tools:`, updatedProgram.toolsByStage[stageToModify].tools.map(t => t.tool));
      console.log(`🗑️ Tools to remove:`, toolsToRemove);

      // Remove specified tools - match by tool name or id
      updatedProgram.toolsByStage[stageToModify].tools = updatedProgram.toolsByStage[stageToModify].tools.filter(
        tool => {
          // Check if the tool should be removed by matching tool name or object structure
          const shouldRemove = toolsToRemove.some(toolToRemove => {
            if (typeof toolToRemove === 'string') {
              return tool.tool === toolToRemove || tool.name === toolToRemove;
            } else if (typeof toolToRemove === 'object') {
              const toolName = toolToRemove.tool || toolToRemove.name;
              return tool.tool === toolName || tool.name === toolName;
            }
            return false;
          });
          return !shouldRemove;
        }
      );

      const newToolsCount = updatedProgram.toolsByStage[stageToModify].tools.length;
      const removedCount = originalToolsCount - newToolsCount;
      console.log(`🗑️ Tools removed: ${removedCount}, remaining: ${newToolsCount}`);
      console.log(`🗑️ Remaining tools:`, updatedProgram.toolsByStage[stageToModify].tools.map(t => t.tool));

      // Recalculate stage total duration
      const oldStageDuration = updatedProgram.toolsByStage[stageToModify].totalDuration;
      updatedProgram.toolsByStage[stageToModify].totalDuration = updatedProgram.toolsByStage[stageToModify].tools.reduce(
        (sum, t) => sum + (t.duration || 0), 0
      );

      console.log(`🗑️ Stage ${stageToModify} duration: ${oldStageDuration} → ${updatedProgram.toolsByStage[stageToModify].totalDuration}`);

      // Recalculate program total duration
      const oldProgramDuration = updatedProgram.totalProgramLength;
      updatedProgram.totalProgramLength = Object.values(updatedProgram.toolsByStage).reduce(
        (sum, stageData) => sum + (stageData.totalDuration || 0), 0
      );

      console.log(`🗑️ Program ${updatedProgram.program_of_work} duration: ${oldProgramDuration} → ${updatedProgram.totalProgramLength}`);

      return updatedProgram;
    };

    let updatedSequence = null;
    let newTotalDuration = 0;
    let updateSuccess = false;

    // Update finalProgramSequence and capture the updated sequence
    console.log("🗑️ Updating finalProgramSequence...");
    this.setters.setFinalProgramSequence(prevSequence => {
      console.log("🗑️ setFinalProgramSequence callback executing...");
      updatedSequence = [...prevSequence];
      const oldProgram = updatedSequence[progIdx];
      updatedSequence[progIdx] = removeToolsFromProgram(updatedSequence[progIdx]);

      // Verify the update was successful
      if (updatedSequence[progIdx] !== oldProgram) {
        updateSuccess = true;
        console.log("🗑️ ✅ Program object reference changed - update successful");
      }

      // Calculate total duration from the updated sequence
      const oldTotalDuration = prevSequence.reduce((sum, program) => 
        sum + (program.totalProgramLength || 0), 0
      );
      newTotalDuration = updatedSequence.reduce((sum, program) => 
        sum + (program.totalProgramLength || 0), 0
      );

      console.log(`🗑️ Total duration change: ${oldTotalDuration} → ${newTotalDuration}`);
      console.log("🗑️ Updated finalProgramSequence callback complete");

      return updatedSequence;
    });

    // Update selectedPrograms with the same data
    console.log("🗑️ Updating selectedPrograms...");
    this.setters.setSelectedPrograms(prevSelected => {
      console.log("🗑️ setSelectedPrograms callback executing...");
      const updatedSelected = [...prevSelected];
      if (updatedSelected[progIdx]) {
        updatedSelected[progIdx] = removeToolsFromProgram(updatedSelected[progIdx]);
        console.log("🗑️ selectedPrograms updated");
      } else {
        console.warn(`🗑️ Program at index ${progIdx} not found in selectedPrograms`);
      }
      return updatedSelected;
    });

    // Update total duration with the calculated value
    console.log(`🗑️ Updating total duration to: ${newTotalDuration}`);
    this.setters.setTotalDuration(newTotalDuration);

    // Mark as unsaved to trigger auto-save
    console.log("🗑️ Marking plan as unsaved");
    this.setters.setIsSaved(false);

    // Verify the tool was actually removed using the updated sequence we captured
    const verifiedProgram = updatedSequence?.[progIdx];
    const verificationPassed = verifiedProgram?.toolsByStage?.[stageToModify]?.tools ? 
      !verifiedProgram.toolsByStage[stageToModify].tools.some(tool => 
        toolsToRemove.some(toolToRemove => {
          if (typeof toolToRemove === 'string') {
            return tool.tool === toolToRemove || tool.name === toolToRemove;
          } else if (typeof toolToRemove === 'object') {
            const toolName = toolToRemove.tool || toolToRemove.name;
            return tool.tool === toolName || tool.name === toolName;
          }
          return false;
        })
      ) : true;

    console.log(`🗑️ State verification: ${verificationPassed ? 'PASSED' : 'FAILED'}`);
    console.log(`🗑️ Verified against updated sequence, not current getter state`);

    // Force component re-render by triggering a state update
    if (verificationPassed) {
      // Force a minimal state change to trigger re-render
      this.setters.setFinalProgramSequence(prevSequence => [...prevSequence]);
      console.log("🗑️ Forced component re-render after verification");
    }

    // Force a save after a delay to ensure persistence
    setTimeout(() => {
      console.log("🗑️ Triggering delayed save after tool removal");
      if (this.savePlan && typeof this.savePlan === 'function') {
        this.savePlan();
      }
    }, 1000);

    const result = {
      success: updateSuccess && verificationPassed,
      message: updateSuccess && verificationPassed ? 
        `Removed ${toolsToRemove.length} tools from ${stageToModify} stage` :
        `Tool removal ${updateSuccess ? 'completed but verification failed' : 'failed'}`,
      removedTools: toolsToRemove,
      updatedProgram: updatedSequence?.[progIdx],
      newTotalDuration,
      stateVerified: verificationPassed,
      debugInfo: {
        programIndex: progIdx,
        stage: stageToModify,
        toolsRemoved: toolsToRemove,
        updateSuccess,
        verificationPassed
      }
    };

    console.log(`🗑️ ${(updateSuccess && verificationPassed) ? '✅' : '❌'} Tool removal complete:`, result);

    return result;
  }

  async executeRemoveToolsFromStages(data, context = {}) {
    const { finalProgramSequence } = context.getters;
    const { setFinalProgramSequence, setSelectedPrograms, setTotalDuration } = context.setters;

    console.log("🗑️ Executing remove_tools_from_stages:", data);

    // Check if confirmation is required for multiple tools
    if (data.requiresConfirmation && !data.confirmed) {
      console.log("⚠️ Multiple tools detected, confirmation required");
      return { 
        success: false, 
        requiresConfirmation: true,
        message: data.confirmationMessage || "This action will affect multiple tools. Please confirm to proceed.",
        multipleToolsDetected: data.multipleToolsDetected,
        pendingAction: data
      };
    }

    // Validate input data
    if (!data || data.programIndex === undefined || !data.stage || !data.tools) {
      console.error("❌ Missing required data for tool removal:", data);
      return { success: false, message: "Missing required data for tool removal" };
    }

    // Execute the removal using the existing method
    return await this.handleRemoveToolsFromStages(data);
  }

  async validateRemoveToolsFromStages(data) {
    console.log("🔍 Validating tool removal data:", data);

    // Check basic required fields
    if (data.programIndex === undefined || !data.stage) {
      console.log("❌ Missing programIndex or stage");
      return false;
    }

    // Normalize and validate tools data - CRITICAL FIX: Check all possible fields
    let toolsToRemove = [];

    if (data.tools && Array.isArray(data.tools)) {
      toolsToRemove = data.tools;
    } else if (data.toolName) {
      toolsToRemove = [data.toolName];
      // CRITICAL: Update the data object to use normalized structure
      data.tools = toolsToRemove;
      delete data.toolName;
    } else if (data.tool) {
      toolsToRemove = [data.tool];
      // CRITICAL: Update the data object to use normalized structure
      data.tools = toolsToRemove;
      delete data.tool;
    }

    if (toolsToRemove.length === 0) {
      console.log("❌ No tools specified for removal");
      return false;
    }

    console.log("✅ Tool removal validation passed:", {
      programIndex: data.programIndex,
      stage: data.stage,
      toolsCount: toolsToRemove.length,
      normalizedTools: toolsToRemove
    });

    return true;
  }

  // FIX #38: Handle bulk program merge - merges tools from incoming programs into existing programs
  async handleBulkProgramMerge(data) {
    const { programs, tools } = data;
    console.log("➕ FIX #38: handleBulkProgramMerge called:", {
      programsCount: programs?.length || 0,
      toolsCount: tools?.length || 0
    });

    const currentPrograms = this.getters.finalProgramSequence || [];
    
    if (!programs || !Array.isArray(programs) || programs.length === 0) {
      console.error("❌ FIX #38: No programs provided for merge");
      return {
        success: false,
        message: "No programs provided for bulk merge",
        error: true
      };
    }

    try {
      // Merge incoming programs with existing programs
      let updatedPrograms = [...currentPrograms];
      let totalToolsAdded = 0;

      programs.forEach((incomingProgram, progIdx) => {
        const programName = incomingProgram.program_of_work;
        console.log(`➕ Processing incoming program: ${programName}`);
        
        // Find matching existing program or add new
        const existingIdx = updatedPrograms.findIndex(p => 
          p.program_of_work === programName
        );
        
        if (existingIdx >= 0) {
          // Merge tools into existing program
          const existingProgram = updatedPrograms[existingIdx];
          const mergedProgram = { ...existingProgram };
          mergedProgram.toolsByStage = { ...existingProgram.toolsByStage };
          
          // Merge each stage's tools
          if (incomingProgram.toolsByStage) {
            Object.entries(incomingProgram.toolsByStage).forEach(([stageName, stageData]) => {
              if (!mergedProgram.toolsByStage[stageName]) {
                mergedProgram.toolsByStage[stageName] = { tools: [], totalDuration: 0 };
              }
              
              // FIX #38: Create separate Sets for IDs AND tool names to properly detect duplicates
              const existingToolIds = new Set(
                mergedProgram.toolsByStage[stageName].tools.map(t => t.id).filter(Boolean)
              );
              const existingToolNames = new Set(
                mergedProgram.toolsByStage[stageName].tools.map(t => (t.tool || t.name || '').toLowerCase().trim()).filter(Boolean)
              );
              
              console.log(`➕ FIX #38: Stage ${stageName} existing tools:`, {
                idCount: existingToolIds.size,
                nameCount: existingToolNames.size,
                names: Array.from(existingToolNames).slice(0, 5)
              });
              
              // Filter out duplicates by checking BOTH ID and name
              const newTools = (stageData.tools || []).filter(tool => {
                const toolId = tool.id;
                const toolName = (tool.tool || tool.name || '').toLowerCase().trim();
                
                // Check if tool already exists by ID OR by name
                const idExists = toolId && existingToolIds.has(toolId);
                const nameExists = toolName && existingToolNames.has(toolName);
                
                if (idExists || nameExists) {
                  console.log(`➕ FIX #38: Skipping duplicate tool: ${toolName} (idExists: ${idExists}, nameExists: ${nameExists})`);
                  return false;
                }
                return true;
              });
              
              console.log(`➕ FIX #38: Adding ${newTools.length} new tools (filtered from ${stageData.tools?.length || 0} incoming)`);
              
              mergedProgram.toolsByStage[stageName].tools = [
                ...mergedProgram.toolsByStage[stageName].tools,
                ...newTools
              ];
              
              // Recalculate stage duration
              mergedProgram.toolsByStage[stageName].totalDuration = 
                mergedProgram.toolsByStage[stageName].tools.reduce(
                  (sum, t) => sum + (t.duration || 0), 0
                );
              
              totalToolsAdded += newTools.length;
            });
          }
          
          // Recalculate program total duration
          mergedProgram.totalProgramLength = Object.values(mergedProgram.toolsByStage).reduce(
            (sum, stageData) => sum + (stageData.totalDuration || 0), 0
          );
          
          updatedPrograms[existingIdx] = mergedProgram;
          console.log(`➕ Merged tools into existing program: ${programName}`);
        } else {
          // Add as new program
          updatedPrograms.push(incomingProgram);
          
          // Count tools in new program
          if (incomingProgram.toolsByStage) {
            Object.values(incomingProgram.toolsByStage).forEach(stageData => {
              totalToolsAdded += (stageData.tools?.length || 0);
            });
          }
          console.log(`➕ Added new program: ${programName}`);
        }
      });

      // Update state
      this.setters.setFinalProgramSequence(updatedPrograms);
      this.setters.setSelectedPrograms(updatedPrograms);
      
      // Recalculate total duration
      const newTotalDuration = updatedPrograms.reduce(
        (sum, prog) => sum + (prog.totalProgramLength || 0), 0
      );
      this.setters.setTotalDuration(newTotalDuration);
      
      // Mark as unsaved
      this.setters.setIsSaved(false);
      
      // Trigger save
      if (this.savePlan) {
        setTimeout(() => {
          this.savePlan();
        }, 500);
      }
      
      console.log(`✅ FIX #38: Bulk merge complete - ${totalToolsAdded} tools added/merged`);
      
      return {
        success: true,
        message: `Successfully merged ${programs.length} program(s) with ${totalToolsAdded} tools`,
        toolsAdded: totalToolsAdded,
        programsUpdated: programs.length
      };
    } catch (error) {
      console.error("❌ FIX #38: Error during bulk merge:", error);
      return {
        success: false,
        message: `Bulk merge failed: ${error.message}`,
        error: true
      };
    }
  }
}

module.exports = ToolManagementCapability;
